import * as React from "react";
import {
  makeStyles,
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  Text,
  Spinner,
} from "@fluentui/react-components";
import { LockClosed24Regular } from "@fluentui/react-icons";

interface LoginProps {
  onLogin: (email: string, pin: string) => Promise<boolean>;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "20px",
    backgroundColor: "#fafafa",
  },
  card: {
    width: "100%",
    maxWidth: "340px",
    padding: "32px",
    backgroundColor: "#ffffff",
    border: "1px solid #e8e8e8",
    borderRadius: "8px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    marginBottom: "24px",
  },
  icon: {
    fontSize: "32px",
    color: "#666666",
  },
  title: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333333",
    margin: "0",
  },
  subtitle: {
    fontSize: "12px",
    color: "#999999",
    margin: "0",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#666666",
  },
  error: {
    color: "#d13438",
    fontSize: "12px",
    textAlign: "center",
    margin: "0",
  },
  button: {
    marginTop: "8px",
  },
});

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const styles = useStyles();
  const [email, setEmail] = React.useState("");
  const [pin, setPin] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !pin) {
      setError("Please enter both email and PIN");
      return;
    }

    setIsLoading(true);

    try {
      const success = await onLogin(email, pin);
      if (!success) {
        setError("Invalid email or PIN. Please try again.");
      }
    } catch (err) {
      setError("Login failed. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <LockClosed24Regular className={styles.icon} />
          <h2 className={styles.title}>Scholar Sync</h2>
          <p className={styles.subtitle}>Sign in to access your papers</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <Label htmlFor="email" required className={styles.label}>
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={isLoading}
              appearance="outline"
            />
          </div>

          <div className={styles.inputGroup}>
            <Label htmlFor="pin" required className={styles.label}>
              PIN
            </Label>
            <Input
              id="pin"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter your PIN"
              disabled={isLoading}
              appearance="outline"
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <Button
            appearance="primary"
            size="medium"
            type="submit"
            disabled={isLoading}
            className={styles.button}
          >
            {isLoading ? <Spinner size="tiny" /> : "Sign In"}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default Login;
